import React, { useState, useCallback } from 'react';
import { useAddin } from '../App';
import Modal from './Modal';
import * as webhook from '../services/webhook';

/**
 * AddNoteModal — add a note to the linked matter in Clio.
 * @param {{ isOpen: boolean, onClose: () => void }} props
 */
export default function AddNoteModal({ isOpen, onClose }) {
    const { linkedMatter, itemId, conversationId, userEmail } = useAddin();
    const [subject, setSubject] = useState('');
    const [noteText, setNoteText] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const handleSubmit = useCallback(async () => {
        if (!noteText.trim()) {
            setFeedback({ type: 'error', message: 'Note text is required' });
            return;
        }

        setLoading(true);
        setFeedback(null);

        try {
            await webhook.addNote({
                outlookItemId: itemId,
                conversationId,
                userEmail,
                matterId: linkedMatter?.id || linkedMatter?.matter_id,
                matterNumber: linkedMatter?.number || linkedMatter?.matter_number,
                subject: subject.trim(),
                noteText: noteText.trim(),
            });
            setFeedback({ type: 'success', message: 'Note added to matter in Clio' });
            setTimeout(() => {
                setSubject('');
                setNoteText('');
                setFeedback(null);
                onClose();
            }, 1500);
        } catch (err) {
            setFeedback({ type: 'error', message: err.message || 'Failed to add note' });
        } finally {
            setLoading(false);
        }
    }, [subject, noteText, itemId, conversationId, userEmail, linkedMatter, onClose]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Note">
            {/* Matter context */}
            {linkedMatter && (
                <div className="info-block" style={{ padding: '8px 11px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--navy)' }}>
                        {linkedMatter.name || linkedMatter.matter_description}
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--grey02)' }}>
                        {linkedMatter.number || linkedMatter.matter_number}
                    </div>
                </div>
            )}

            <div className="form-group">
                <label className="form-label">Subject</label>
                <input
                    type="text"
                    className="form-input"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter subject..."
                    id="note-subject"
                    autoFocus
                />
            </div>

            <div className="form-group">
                <label className="form-label">Note</label>
                <textarea
                    className="form-textarea"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Enter note text..."
                    style={{ minHeight: '120px' }}
                    id="note-text"
                />
            </div>

            {/* Feedback */}
            {feedback && (
                <div className={`modal-feedback ${feedback.type}`}>
                    {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                </div>
            )}

            <button
                className="modal-submit"
                onClick={handleSubmit}
                disabled={loading || !noteText.trim()}
                id="submit-note-btn"
            >
                {loading && <div className="btn-spinner" />}
                {loading ? 'Saving...' : 'Add Note to Matter'}
            </button>
        </Modal>
    );
}
